// ============================================================
// 7. LowStockReportPage.tsx
// ============================================================
// src/features/reports/pages/LowStockReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getLowStockReport, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import { AlertTriangle, Package, DollarSign, ShoppingCart, AlertOctagon } from 'lucide-react';

export function LowStockReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [statusFilter, setStatusFilter] = useState<'all' | 'out_of_stock' | 'low'>('all');

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const { data, isLoading } = useQuery({
        queryKey: ['report-low-stock', tenantId, branchId],
        queryFn: () => getLowStockReport(tenantId, branchId || undefined),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'low_stock', format, { branchId });
        window.open(url, '_blank');
    };

    const hasFilters = !!(branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const items = data?.items || [];
    const filteredItems = statusFilter === 'all'
        ? items
        : items.filter((i: any) => i.status === statusFilter);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            <ReportHeader
                title="Low Stock Report"
                description="Products below reorder threshold"
                icon={AlertTriangle}
                iconGradient="from-amber-500 to-orange-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || items.length === 0 ? (
                    <EmptyState
                        icon={<Package className="w-10 h-10" />}
                        title="No Low Stock Items"
                        description="All products are adequately stocked. Great job!"
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Low Stock Items"
                                value={String(data.summary?.totalLowStock || 0)}
                                icon={AlertTriangle}
                                gradient="from-amber-500 to-orange-600"
                            />
                            <SummaryCard
                                label="Out of Stock"
                                value={String(data.summary?.outOfStock || 0)}
                                icon={AlertOctagon}
                                gradient="from-red-500 to-rose-600"
                            />
                            <SummaryCard
                                label="Critical (< 5)"
                                value={String(data.summary?.critical || 0)}
                                icon={Package}
                                gradient="from-orange-500 to-yellow-600"
                            />
                            <SummaryCard
                                label="Reorder Cost"
                                value={formatNaira(data.summary?.totalReorderCost || 0)}
                                icon={DollarSign}
                                gradient="from-slate-500 to-slate-600"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="flex gap-2">
                            {(['all', 'out_of_stock', 'low'] as const).map(status => (
                                <button
                                    key={status}
                                    onClick={() => setStatusFilter(status)}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg capitalize ${statusFilter === status
                                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                            : 'text-slate-500 border border-slate-200 hover:bg-slate-50'
                                        }`}
                                >
                                    {status.replace('_', ' ')} ({status === 'all' ? items.length : items.filter((i: any) => i.status === status).length})
                                </button>
                            ))}
                        </div>

                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Category</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Qty On Hand</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Threshold</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Reorder Point</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Reorder Qty</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost Price</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Reorder Value</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {filteredItems.map((item: any, i: number) => (
                                            <tr key={i} className={`hover:bg-slate-50 transition-colors ${item.status === 'out_of_stock' ? 'bg-red-50/30' : ''
                                                }`}>
                                                <td className="px-6 py-4">
                                                    <span className="text-sm font-medium text-slate-900">{item.productName}</span>
                                                    <p className="text-xs text-slate-400">{item.branchName}</p>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-500">{item.categoryName}</td>
                                                <td className="px-6 py-4 text-right">
                                                    <span className={`text-sm font-bold ${item.quantityOnHand <= 0 ? 'text-red-600' :
                                                            item.quantityOnHand <= 5 ? 'text-amber-600' : 'text-slate-900'
                                                        }`}>
                                                        {item.quantityOnHand}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center text-sm text-slate-500">
                                                    {item.lowStockThreshold}
                                                </td>
                                                <td className="px-6 py-4 text-center text-sm text-slate-500">
                                                    {item.reorderPoint}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                    {item.reorderQuantity}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                    {formatNaira(item.costPrice)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold text-amber-600">
                                                    {formatNaira(item.reorderValue)}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge
                                                        variant={item.status === 'out_of_stock' ? 'danger' : 'warning'}
                                                        size="sm"
                                                        pulse={item.status === 'out_of_stock'}
                                                    >
                                                        {item.status === 'out_of_stock' ? 'OUT OF STOCK' : 'LOW'}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}